import { useEffect, useRef } from "react";

import { MessageDescriptor } from "@lingui/core";
import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { map } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import ArrowBack from "@mui/icons-material/ArrowBack";
import { Box, Typography } from "@mui/material";

import { Container } from "@app/analytics/account/components/styles";
import ActionBar from "@app/analytics/components/ActionBar";
import ActionBarButton from "@app/analytics/components/ActionBar/ActionBarButton";
import useSectionScroll from "@app/analytics/hooks/domHelpers/useSectionScroll";
import useAccountSettings from "@app/analytics/hooks/settings/useAccountSettings";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { usePageTitle } from "@app/utils/hooks";

import AlertBanner from "@rui/AlertBanner";
import Stack, { Row } from "@rui/Stack";
import { StickyContainer } from "@rui/styles";
import Tab from "@rui/Tab";
import Tabs from "@rui/Tabs";

import AccountModule from "./AccountModule";
import { UserSettingsSection, USER_SETTINGS_ROUTES } from "./constants";
import CustomVocabularyModule from "./CustomVocabularyModule";
import DeleteAccount from "./DeleteAccount";
import EmailPreferencesModule from "./EmailPreferencesModule";
import IntegrationsModule from "./IntegrationsModule";
import OtherModule from "./OtherModule";
import ReadAssistantModule from "./ReadAssistantModule";
import RecordingAndPlaybackModule from "./RecordingAndPlaybackModule";
import ReportingModule from "./ReportingModule";
import SmartSchedulingModule from "./SmartSchedulingModule";
import TaggingModule from "./TaggingModule";

const DEFAULT_ACTIVE_SECTION = "profile";
const SCROLL_THRESHOLD = 250;

interface SettingsTab {
  id: UserSettingsSection;
  label: MessageDescriptor;
  path: string;
  enabled?: () => boolean;
  component: typeof AccountModule;
}

const TAB_CONFIG: {
  [key in UserSettingsSection]: {
    label: MessageDescriptor;
    component: typeof AccountModule;
  };
} = {
  profile: {
    label: msg`Profile & Account`,
    component: AccountModule,
  },
  integrations: {
    label: msg`Integrations`,
    component: IntegrationsModule,
  },
  assistant: {
    label: msg`Meeting Assistant`,
    component: ReadAssistantModule,
  },
  recordingAndPlayback: {
    label: msg`Recording & Playback`,
    component: RecordingAndPlaybackModule,
  },
  reporting: {
    label: msg`Reporting`,
    component: ReportingModule,
  },
  smartScheduler: {
    label: msg`Smart Scheduler`,
    component: SmartSchedulingModule,
  },
  tagging: {
    label: msg`Tagging`,
    component: TaggingModule,
  },
  customVocabulary: {
    label: msg`Custom Vocabulary`,
    component: CustomVocabularyModule,
  },
  email: {
    label: msg`Email`,
    component: EmailPreferencesModule,
  },
  other: {
    label: msg`Other`,
    component: OtherModule,
  },
};

const SETTINGS_TABS: SettingsTab[] = USER_SETTINGS_ROUTES.map((route) => ({
  ...TAB_CONFIG[route.sectionId],
  id: route.sectionId,
  path: route.route,
}));

const UserPreferences = ({ sectionId }: { sectionId: UserSettingsSection }) => {
  const { _ } = useLingui();

  usePageTitle("User Preferences");

  const navigate = useNavigate();
  const auth = useAuthenticated();
  const { resendEmailVerification } = useAccountSettings();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleResendVerification = () => {
    resendEmailVerification.mutate();
  };

  const handleSectionChange = (section: string) => {
    const path = SETTINGS_TABS.find(({ id }) => id === section)?.path;
    if (path) navigate(path, { replace: true });
  };

  const enabledTabs = SETTINGS_TABS.filter((t) => t.enabled?.() ?? true);

  const [selectedSection, setSelectedSection] = useSectionScroll({
    autoSelectThreshold: SCROLL_THRESHOLD,
    defaultSection: DEFAULT_ACTIVE_SECTION,
    initialScrollDelay: 750,
    preselectedSection: sectionId,
    onSectionChange: handleSectionChange,
    scrollContainer: scrollContainerRef,
    sections: map("id", enabledTabs),
  });

  useEffect(() => {
    if (sectionId !== selectedSection) {
      setSelectedSection(sectionId, true);
    }
  }, [sectionId]);

  const showEmailVerificationBanner = !auth.user?.isEmailVerified;
  const handleSelectTab = (selectedSectionId: UserSettingsSection) => () =>
    setSelectedSection(selectedSectionId, true);

  return (
    <Stack sx={{ height: "100vh", overflow: "hidden" }}>
      <StickyContainer>
        <ActionBar>
          <Row variant="left" spacing={1}>
            <ActionBarButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </ActionBarButton>
            <Typography variant="h2" sx={{ color: "shades.neutral.700" }}>
              <Trans>Account Settings</Trans>
            </Typography>
          </Row>
        </ActionBar>
        <Box
          sx={{
            width: "100%",
            bgcolor: "#FFF",
            zIndex: 8,
            paddingTop: 1,
            borderBottom: (theme) =>
              `2px solid ${theme.palette.shades.neutral[100]}`,
          }}
        >
          <Row variant="center" sx={{ mb: "-2px" }}>
            <Tabs value={selectedSection} variant="scrollable">
              {enabledTabs.map((tab) => (
                <Tab
                  key={tab.id}
                  label={_(tab.label.id)}
                  value={tab.id}
                  onClick={handleSelectTab(tab.id)}
                />
              ))}
            </Tabs>
          </Row>
        </Box>
        {showEmailVerificationBanner && (
          <AlertBanner
            severity="warning"
            title="Email verification"
            action={{
              label: "Resend Verification",
              onClick: handleResendVerification,
            }}
          >
            <Trans>
              Your email address has not been verified. Please click the magic
              link in your inbox to verify your email.
            </Trans>
          </AlertBanner>
        )}
      </StickyContainer>
      <Stack
        ref={scrollContainerRef}
        sx={{
          bgcolor: "#FFF",
          overflow: "auto",
          position: "relative",
        }}
      >
        <Container sx={{ pt: 2 }}>
          {enabledTabs.map(({ id, component: TabComponent, label }) => (
            <TabComponent
              key={id}
              id={id}
              active={selectedSection === id}
              title={_(label)}
            />
          ))}
          <DeleteAccount />
        </Container>
      </Stack>
    </Stack>
  );
};

export default UserPreferences;
